import { BuyerCustomerDTO, CancellationType, InvoiceParamsFromPosDTO, PrintPreviewDTO, Res, SaleDTO, TicketDTO, TicketSummaryDTO } from 'core/api';
import { CustomError, ServerError, handleServerErrors, Events, Result } from 'core/infrastructure';
import { OrderingFacade, PrintFacade } from 'pos/facades';


let ticket: Nullable<TicketDTO>;
let preview: Nullable<PrintPreviewDTO>;
let pendingTickets: TicketSummaryDTO[] = [];

export function getSelectedTicket(): Nullable<TicketDTO> {
	return ticket;
}

export function getPreview(): Nullable<PrintPreviewDTO> {
	return preview;
}

export function getSelectedSummary(): Nullable<TicketSummaryDTO> {
	return pendingTickets.find(x => ticket && x.Id === ticket.Id) || null;
}

export function getPendingTickets(): TicketSummaryDTO[] {
	return pendingTickets;
}

function ensureTicketIsSelected() {
	if (ticket === null)
		throw new Error('No se ha seleccionado ningún ticket');
}

export async function setFriendlyName(name: string): Promise<void> {
	ensureTicketIsSelected();
	await run((async () => OrderingFacade.setFriendlyName(ticket!.Id, name)));
	await reloadPendingTickets();
}

export async function setTableCompanions(tableCompanions: Nullable<number>): Promise<Result<void, CustomError>> {
	ensureTicketIsSelected();
	const result = await run(async () => OrderingFacade.setTableCompanions(ticket!.Id, tableCompanions));
	if (!result.ok)
		return result;

	await reloadPendingTickets();
	return Result.ok();
}

export async function refund(params: InvoiceParamsFromPosDTO): Promise<Result<SaleDTO, CustomError>> {
	ensureTicketIsSelected();
	const result = await run(() => OrderingFacade.refundTicket(ticket!.Id, params));
	await reloadPendingTickets();
	return result;
}

export async function setCustomer(customer: BuyerCustomerDTO): Promise<void> {
	ensureTicketIsSelected();
	await run(() => OrderingFacade.setBuyerCustomer(ticket!.Id, customer));
	await reloadPendingTickets();
}

export async function close(params: InvoiceParamsFromPosDTO): Promise<Result<SaleDTO, CustomError>> {
	ensureTicketIsSelected();
	const result = await run(() => OrderingFacade.closeTicket(ticket!.Id, params));
	await reloadPendingTickets();

	if (result.ok)
		await Events.raise('pos:sale:ticket-closed-on-close-day', { invoiceId: result.value.DocumentId, invoiceType: params.InvoiceType });

	return result;
}

export async function select(selected: TicketSummaryDTO): Promise<void> {
	await run(async () => {
		ticket = await OrderingFacade.getTicket(selected.Id);
		preview = (await PrintFacade.getTicketPreviews(selected.Id))[0];
	});
}

export async function reloadPendingTickets(): Promise<void> {
	pendingTickets = await OrderingFacade.getOpenedTickets();
	const summary = pendingTickets.find(x => ticket && ticket.Id === x.Id) || pendingTickets.last() || null;
	if (summary) {
		await select(summary);
	}
	else {
		ticket = null;
		preview = null;
	}
}

export function ticketCanBeClosedAsRefund(): boolean {
	ensureTicketIsSelected();
	return ticket!.NetAmount < 0 && ticket!.Lines.some(x => x.Quantity < 0);
}

export async function cancel(type: CancellationType, reason: string): Promise<void> {
	ensureTicketIsSelected();
	await run(async () => OrderingFacade.cancelTicket(ticket!.Id, type, reason));
	await reloadPendingTickets();
}

export async function release(): Promise<void> {
	ensureTicketIsSelected();
	await run(async () => OrderingFacade.releaseTicket(ticket!.Id, true));
	await reloadPendingTickets();
}

async function run<T>(fn: () => Promise<T>): Promise<Result<T, CustomError>> {
	const handledError: CustomError = new ServerError('ERROR NO CAPTURADO');

	const result = await handleServerErrors(fn, {

		onEmptyTicketError: async (_error) => {
			await Events.raise('pos:close-day:operation-failed', { msg: Res.Msg.MSG_TICKET_HAS_BEEN_MODIFIED_AND_NOW_IT_IS_EMPTY() });
		},
		onTicketNotFoundError: async (_error) => {
			await Events.raise('pos:close-day:operation-failed', { msg: Res.Msg.MSG_TICKET_HAS_BEEN_DELETED_OR_CLOSED_BY_ANOTHER_USER() });
		},
		onFrozenTicketError: async (_error) => {
			await Events.raise('pos:close-day:operation-failed', { msg: Res.Msg.MSG_CANNOT_MODIFY_FROZEN_TICKET() });
		},
		onFrozenPaymentsError: async (_error) => {
			await Events.raise('pos:close-day:operation-failed', { msg: Res.Msg.MSG_TICKET_HAS_FROZEN_PAYMENTS_SO_CHECK_THE_NEW_COMPOSITION() });
		},
		onPrintError: async (error) => {
			await Events.raise('pos:close-day:operation-failed', { msg: Res.Msg.MSG_TICKET_COULD_NOT_BE_PRINTED(error.message) });
		},
		onInvalidPaymentError: async (error) => {
			await Events.raise('pos:close-day:operation-failed', { msg: error.message });
		},
		onDomainError: async (error) => {
			await Events.raise('pos:close-day:operation-failed', { msg: error.message });
		},
		onIntegrationAuthorizationError: async error => {
			await Events.raise('pos:close-day:operation-failed', { msg: Res.Msg.MSG_INTEGRATION_AUTHORIZATION_ERROR(error.message) });
		},
		onInvalidCustomerCifError: async _error => {
			await Events.raise('pos:close-day:operation-failed', { msg: Res.Msg.MSG_INVALID_CUSTOMER_CIF_TO_CREATE_STANDARD_INVOICE() });
		},
	});

	return result.ok ? Result.ok(result.value) : Result.error(handledError);
}
